import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function DoctorProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/doctors/${id}/profile`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div>Loading...</div>;
  const { doctor: d, stats, opds, ipds } = data;

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={() => nav("/doctors")} className="rounded-sm"><ArrowLeft className="w-4 h-4 mr-1"/> Back to Doctors</Button>

      <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-4">
        <img src={d.photo || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200"} alt="" className="w-20 h-20 object-cover rounded-sm" />
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-xl font-semibold">{d.name}</h1>
            <Badge className={`rounded-sm ${d.status === "active" ? "bg-teal-100 text-teal-800" : "bg-slate-100"}`}>{d.status}</Badge>
          </div>
          <div className="text-sm text-slate-600">{d.department} · {d.qualification}</div>
          <div className="text-xs text-slate-500 mt-1">Reg No: {d.reg_no} · {d.mobile}</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border p-3"><div className="label-tiny">Total OPD Visits</div><div className="font-mono text-2xl mt-1">{stats.total_opd}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Total IPD Admissions</div><div className="font-mono text-2xl mt-1">{stats.total_ipd}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Unique Patients</div><div className="font-mono text-2xl mt-1">{stats.unique_patients}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Gross Revenue</div><div className="font-mono text-2xl mt-1 text-blue-700">{fmtINR(stats.gross_revenue)}</div></div>
      </div>

      <div className="grid grid-cols-6 gap-3">
        <div className="bg-white border p-3"><div className="label-tiny">OPD Fee</div><div className="font-mono mt-1">{fmtINR(d.opd_charge)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">IPD Visit</div><div className="font-mono mt-1">{fmtINR(d.ipd_charge)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Emergency</div><div className="font-mono mt-1">{fmtINR(d.emergency_charge)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">OPD Comm</div><div className="font-mono mt-1">{d.opd_commission}%</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">IPD Comm</div><div className="font-mono mt-1">{d.ipd_commission}%</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">OT Comm</div><div className="font-mono mt-1">{d.ot_commission}%</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border">
          <div className="p-3 border-b label-tiny">Recent OPD Visits ({opds.length})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">OPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Date</th></tr></thead>
            <tbody>
              {opds.slice(0, 20).map((o) => (
                <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/opd/${o.id}`)}>
                  <td className="p-2 font-mono text-xs">{o.opd_no}</td><td>{o.patient_name}</td><td className="text-xs">{fmtDT(o.created_at)}</td>
                </tr>
              ))}
              {opds.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-sm text-slate-500">No OPDs yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border">
          <div className="p-3 border-b label-tiny">IPD Admissions ({ipds.length})</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">IPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Status</th></tr></thead>
            <tbody>
              {ipds.slice(0, 20).map((o) => (
                <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/ipd/${o.id}`)}>
                  <td className="p-2 font-mono text-xs">{o.ipd_no}</td><td>{o.patient_name}</td><td className="text-xs">{o.status}</td>
                </tr>
              ))}
              {ipds.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-sm text-slate-500">No IPDs yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
